import * as React from 'react';
import { createPortal } from 'react-dom';
import styled, {
  createGlobalStyle,
} from 'styled-components';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

const ModalWindow = ({
  children,
  onClose,
  className = '',
}: Props) => {
  // Backdrop-click closes; clicks inside the content don't bubble out. We do NOT
  // use ClickAwayListener here — a ClickAwayListener mounted as a result of a click
  // can catch that same click and immediately close the dialog (the modal opened
  // then vanished in the same tick on the production build).
  // Render through a portal on <body> so the fixed overlay can't be hidden behind a
  // page's chrome / trapped inside an ancestor's stacking context, and so the click
  // that opened it never travels through this backdrop.
  const overlay = (
    <ModalContainer
      className={className}
      role="presentation"
      onClick={onClose}
    >
      <GlobalStyles />
      <ModalContent
        role="dialog"
        aria-modal="true"
        onClick={e => e.stopPropagation()}
      >
        <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton>
        {children}
      </ModalContent>
    </ModalContainer>
  );
  return typeof document !== 'undefined'
    ? createPortal(overlay, document.body)
    : overlay;
};

const GlobalStyles = createGlobalStyle`
  body {
    max-height: 100vh;
    overflow: hidden;
  }
`;

const ModalContainer = styled.div`
  position: fixed;
  z-index: var(--modalZIndex);
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  padding: 40px 20px;
  overflow: auto;
  display: flex;
  background-color: rgba(19, 23, 32, 0.45);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
`;

const ModalContent = styled.div.attrs({
  className: 'modal-window__modal-content',
})`
  position: relative;
  margin: auto;
  background-color: var(--neutral-0);
  width: 100%;
  max-width: 630px;
  display: flex;
  justify-content: center;
  padding: var(--space-10) var(--space-12);
  border-radius: var(--radius-lg);
  border: 1px solid var(--neutral-200);
  box-shadow: var(--shadow-lg);
`;

const CloseButton = styled(IconButton)`
  && {
    position: absolute;
    top: 12px;
    right: 12px;
    padding: 6px;
    margin: 0;
    color: var(--neutral-500);
    background: transparent;
    transition: background-color 0.15s ease, color 0.15s ease;
    z-index: 2;
  }

  &&:hover {
    background: var(--neutral-100);
    color: var(--mainText);
  }

  &&:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  svg {
    font-size: 1.25rem;
  }
`;

interface Props {
  children: React.ReactNode | React.ReactNodeArray;
  onClose: () => void;
  className?: string;
}

export default ModalWindow;
